/**
 * Blinkit-theme "My orders" (``/orders``). Orders / Bookings tabs and all data + actions come from
 * useOrdersController (the classic page's logic); the Bookings tab reuses the classic booking list and dialogs
 * (BookingsPanel) unchanged. The Orders tab adds status filters (All / Active / Delivered / Cancelled) and cards with
 * item thumbnails, status, date, total and the rate prompt for delivered orders.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ChevronRight, Clock, Package, Receipt, Star, XCircle } from "lucide-react";
import { fmtInr } from "../../../lib/priceBreakdown";
import { BkChip, BkEmptyState, BkPageHeader } from "../../../components/blinkit/BkUI";
import { useOrdersController } from "../controllers/useOrdersController";
import { STATUS_FALLBACK } from "../controllers/useOrderTrackController";
import { BookingModals, BookingsList } from "../_partials/BookingsPanel";
import { OrderGroupChip } from "../../../components/MultiShop";

const CLOSED_BAD = new Set(["CANCELLED", "REJECTED"]);
const FILTERS = [
    { key: "all", label: "All", test: () => true },
    { key: "active", label: "Active", test: (o) => o.status !== "DELIVERED" && !CLOSED_BAD.has(o.status) },
    { key: "delivered", label: "Delivered", test: (o) => o.status === "DELIVERED" },
    { key: "cancelled", label: "Cancelled", test: (o) => CLOSED_BAD.has(o.status) },
];

export function orderStatusUi(status) {
    const label = (STATUS_FALLBACK[status] || {}).label || String(status || "").replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
    if (status === "DELIVERED") return { label, Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    if (CLOSED_BAD.has(status)) return { label, Icon: XCircle, cls: "bg-destructive/10 text-destructive border-destructive/25" };
    return { label, Icon: Clock, cls: "bg-primary/10 text-primary border-primary/25" };
}

const fmtWhen = (ts) => {
    try {
        return new Date(ts).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
};

function OrderCard({ o }) {
    const st = orderStatusUi(o.status);
    const items = o.items || [];
    const thumbs = items.map((i) => i.image_url).filter(Boolean).slice(0, 4);
    const count = items.reduce((n, i) => n + (i.quantity || 0), 0);
    return (
        <Link to={`/orders/${o.id}`} data-testid={`order-${o.id}`}
              className="block bg-card rounded-2xl border border-border/80 p-4 active:scale-[0.995] transition hover:border-foreground/20">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-[15px] font-extrabold leading-tight truncate">{o.restaurant_name}</p>
                    <p className="text-[12px] text-muted-foreground mt-0.5 truncate">{o.order_unique_id} · {fmtWhen(o.created_at)}</p>
                    {/* Only on an order that came from a multi-shop checkout. */}
                    <OrderGroupChip order={o} className="mt-1.5" />
                </div>
                <span className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${st.cls}`} data-testid={`order-status-${o.id}`}>
                    <st.Icon size={12} /> {st.label}
                </span>
            </div>
            <div className="mt-3 flex items-center gap-2">
                <div className="flex -space-x-2 shrink-0" aria-hidden="true">
                    {thumbs.length ? thumbs.map((src, k) => (
                        <span key={k} className="w-10 h-10 rounded-lg border-2 border-card bg-muted overflow-hidden"><img src={src} alt="" className="w-full h-full object-cover" loading="lazy" /></span>
                    )) : <span className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center"><Package size={16} className="text-muted-foreground" /></span>}
                </div>
                <p className="min-w-0 flex-1 text-[12.5px] text-muted-foreground line-clamp-2 leading-snug">
                    {items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}
                </p>
            </div>
            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-3">
                <span className="text-[14px] font-extrabold tabular-nums">{fmtInr(o.totals?.grand_total)}<span className="ml-1.5 text-[12px] font-medium text-muted-foreground">· {count} item{count === 1 ? "" : "s"}</span></span>
                <span className="inline-flex items-center gap-3 shrink-0">
                    {o.status === "DELIVERED" && (
                        <span data-testid={`rate-hint-${o.id}`} className="text-[12px] font-bold text-amber-700 inline-flex items-center gap-1">
                            <Star size={13} className="fill-current" /> Rate
                        </span>
                    )}
                    <span className="text-[12.5px] font-bold text-primary inline-flex items-center">Details <ChevronRight size={15} /></span>
                </span>
            </div>
        </Link>
    );
}

export default function BkOrdersView() {
    const {
        tab, setTab, orders, bookings, counts, cancelBooking, paying, setPaying, openPay, submitPay, reviewing,
        setReviewing, openReview, submitReview,
    } = useOrdersController();
    const [filter, setFilter] = useState("all");
    const shown = useMemo(() => (orders || []).filter(FILTERS.find((f) => f.key === filter).test), [orders, filter]);

    return (
        <div className="pb-32 min-w-0" data-testid="bk-orders-page">
            <BkPageHeader title="My orders" back="/" testid="bk-orders-header">
                <div className="px-4 pb-2.5 flex gap-2 overflow-x-auto no-scrollbar" data-testid="orders-tabs">
                    <BkChip active={tab === "orders"} onClick={() => setTab("orders")} testid="orders-tab-orders">
                        <Receipt size={14} /> Orders{counts.orders ? <span className="tabular-nums opacity-70">{counts.orders}</span> : null}
                    </BkChip>
                    <BkChip active={tab === "bookings"} onClick={() => setTab("bookings")} testid="orders-tab-bookings">
                        Bookings{counts.bookings ? <span className="tabular-nums opacity-70">{counts.bookings}</span> : null}
                    </BkChip>
                </div>
            </BkPageHeader>

            {tab === "orders" ? (
                orders === null ? (
                    <div className="px-4 pt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" aria-hidden="true" data-testid="bk-orders-loading">
                        {[0, 1, 2].map((i) => <div key={i} className="h-[150px] rounded-2xl bg-card border border-border/80 animate-pulse" />)}
                    </div>
                ) : orders.length === 0 ? (
                    <BkEmptyState icon={Receipt} testid="orders-empty" title="No orders yet"
                                  body="Your orders will show up here, with live status and delivery details."
                                  action={<Link to="/" className="inline-flex h-11 items-center px-6 rounded-xl bg-primary text-primary-foreground text-[14px] font-bold">Start shopping</Link>} />
                ) : (
                    <>
                        <div className="px-4 pt-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="bk-order-filters">
                            {FILTERS.map((f) => {
                                const n = (orders || []).filter(f.test).length;
                                return (
                                    <BkChip key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)} testid={`bk-order-filter-${f.key}`}>
                                        {f.label} <span className="tabular-nums opacity-70">{n}</span>
                                    </BkChip>
                                );
                            })}
                        </div>
                        {shown.length === 0 ? (
                            <BkEmptyState icon={Receipt} testid="bk-orders-filter-empty" title={`No ${filter} orders`}
                                          body="Orders with this status will appear here."
                                          action={<button type="button" onClick={() => setFilter("all")} className="h-10 px-5 rounded-xl bg-primary text-primary-foreground text-[13px] font-bold">Show all orders</button>} />
                        ) : (
                            <div className="px-4 pt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="orders-list">
                                {shown.map((o) => <OrderCard key={o.id} o={o} />)}
                            </div>
                        )}
                    </>
                )
            ) : (
                <div className="[&>ul]:pt-3 [&>div]:pt-3">
                    <BookingsList bookings={bookings} cancelBooking={cancelBooking} openPay={openPay} openReview={openReview} />
                </div>
            )}

            <BookingModals paying={paying} setPaying={setPaying} submitPay={submitPay} reviewing={reviewing} setReviewing={setReviewing} submitReview={submitReview} />
        </div>
    );
}
